import { createSelector } from '@reduxjs/toolkit';

import { selectUserSelections } from '~Betslip/selectors/baseBetslipSelectors';
import { selectSelectionDetails } from '~Betslip/selectors/selectionDetailsSelectors';
import { isCombinationSelection, isBetBuilderCombinationSelection } from '~Betslip/utils/combinationsUtils';

import { isSelectionSuspended } from '~Betslip/utils/selectionChecks';

import { GTP_BET_BUILDER_COMBINATION_TYPE } from '~Betslip/uiModes/shared/GTPBetBuilder/constants';

/**
 * A combination is a "meta-selection" which contains other bets. For betting purposes it is
 * considered a single bet. BetBuilder is a combination type
 * In general try to keep handling of combinations generic, unless there is some special logic
 * required to handle a combination type
 *
 * Add any generic selectors to the top of this file, and combination_type specific ones below that
 */

export const selectCombinations = createSelector(
    selectSelectionDetails,
    selections => Object.values(selections).filter(isCombinationSelection),
);

/**
 * @see GTP_BET_BUILDER_COMBINATION_TYPE in GTPBetBuilder/constants
 */
const selectCombinationsByType = createSelector(
    selectCombinations,
    (_state, type) => type,
    (combinations, type) => combinations.filter(({ combination_type }) => combination_type === type),
);

/**
 * @returns {Number} Number of combination selections
 */
export const selectCombinationsCount = createSelector(
    selectCombinations,
    combinations => combinations.length,
);

/**
 * @see generateCombinationId() in Betslip/utils/combinationsUtils.js
 */
export const selectCombinationById = createSelector(
    selectCombinations,
    (_state, combinationId) => combinationId,
    (combinations, combinationId) => {
        if (!combinations.length) {

            return undefined;
        }

        return combinations.find(({ id }) => id === combinationId);
    },
);

/**
 * Object of selection ids -> underlying selection ids
 */
export const selectCombinationToSelectionIdsObject = createSelector(
    selectCombinations,
    combinations => combinations.reduce(
        (accCombinations, { id, selections }) => ({ ...accCombinations, [id]: selections }),
        {},
    ),
);

/**
 * Combination Ids not stale (i.e. not pending fetchBetslipData)
 * (used in BS to register for WS updates)
 */
export const selectCountCombinationSelectionsNotStale = createSelector(
    selectCombinations,
    combinations => combinations.reduce(
        (notStaleCount, { stale }) => {
            if (!stale) {

                return notStaleCount + 1;
            }

            return notStaleCount;
        },
        0,
    ),
);

/**
 * Selection ids belonging to one combination
 */
export const selectOneCombinationSelectionIds = createSelector(
    (state, id) => selectCombinationById(state, id),
    combination => {
        if (!combination) {

            return [];
        }

        return combination.selections.map(id => Number(id));
    },
);

/**
 * Selections belonging to one combination
 */
export const selectOneCombinationSelections = createSelector(
    (state, id) => selectOneCombinationSelectionIds(state, id),
    selectUserSelections,
    (selectionIds, userSelections) => (
        userSelections.filter(({ id }) => selectionIds.includes(Number(id)))
    ),
);

/**
 * All selection ids belonging to any combination
 */
export const selectAllCombinationSelectionIds = createSelector(
    selectCombinations,
    combinations => combinations.flatMap(({ selections }) => selections),
);

/**
 * Selection ids that do not belong to a combination
 */
export const selectNonCombinationSelections = createSelector(
    selectAllCombinationSelectionIds,
    selectUserSelections,
    (selectionIdsInCombination, userSelections) => (
        userSelections.filter(
            selection => !isCombinationSelection(selection) && !selectionIdsInCombination.includes(Number(selection.id))
        )
    ),
);

/**
 * Count of selection ids that do not belong to a combination
 */
export const selectNonCombinationSelectionsCount = createSelector(
    selectNonCombinationSelections,
    selections => selections.length,
);

export const selectCombinationsWithNoPrice = createSelector(
    selectCombinations,
    (selections) => selections.reduce(
        (noPriceSelections, selection) => {
            const { id, combination_type, decimal_price, stake } = selection;

            return (combination_type && !decimal_price)
                ? noPriceSelections.concat([{ id, stake }])
                : noPriceSelections;
        },
        [],
    ),
);

/**
 * Selector - are all combinations betable?
 */
export const selectIsAllCombinationsBetable = createSelector(
    selectCombinations,
    selectSelectionDetails,
    (combinations, selectionsDetails) => {
        const anyCombinationNotBetable = combinations.some(
            ({ ws_suspended, errors, selections }) => {
                if (ws_suspended || (errors !== null && errors.length)) {

                    return true;
                }
                const anyUnderlyingNotBetable = selections.some(selectionId => isSelectionSuspended(selectionsDetails[selectionId]));
                if (anyUnderlyingNotBetable) {

                    return true;
                }

                return false;
            },
        );

        if (anyCombinationNotBetable) {

            return false;
        }

        return true;
    },
);

/**
 * Selector to support business logic - one single combination appears on the parlay/acca tab
 */
export const selectIsOneCombinationOnParlayTab = createSelector(
    selectCombinationsCount,
    selectNonCombinationSelectionsCount,
    (combinationsCount, nonCombinationsCount) => combinationsCount === 1 && !nonCombinationsCount,
);

/**
 * Notification selectors
 */

/**
 * Select the first combination error (for cases where we only display one message)
 */
export const selectFirstCombinationError = createSelector(
    selectCombinations,
    combinations => combinations.find(({ errors, ws_suspended }) => ws_suspended !== true && errors?.[0])?.errors?.[0],
);

/**
 * Check if there are more than one error across all combinations
 */
export const selectHasMultipleCombinationErrors = createSelector(
    selectCombinations,
    combinations => combinations.flatMap(({ errors }) => errors || []).length > 1,
);

/**
 * BetBuilder Combination selectors
 */

const selectBetBuilderCombinations = state => selectCombinationsByType(state, GTP_BET_BUILDER_COMBINATION_TYPE);

export const selectHasBetBuilderCombination = createSelector(
    selectBetBuilderCombinations,
    combinations => combinations.length > 0,
);

export const selectBetBuilderCombinationIds = createSelector(
    selectBetBuilderCombinations,
    combinations => combinations.map(({ id }) => id),
);

export const selectAvailableBetBuilderType = createSelector(
    selectBetBuilderCombinations,
    selectNonCombinationSelectionsCount,
    (combinations, nonBetBuilderSelectionsLength) => {
        const combinationLength = combinations.length;
        const isBetBuilder = combinationLength === 1 && !nonBetBuilderSelectionsLength;
        const isBetBuilderPlus = combinationLength > 1 || (combinationLength === 1 && nonBetBuilderSelectionsLength > 0);

        return {
            isBetBuilder,
            isBetBuilderPlus,
        };
    }
);

export const selectHasAnyBetBuilderCombinationNonZeroStake = createSelector(
    selectUserSelections,
    userSelections => userSelections.some(
        selection => isBetBuilderCombinationSelection(selection) && selection.stake > 0,
    ),
);

/**
 * Object of selection ids -> underlying selection ids that are NOT in error (used in BS to
 * register for WS updates)
 */
export const selectBetBuilderCombinationSelectionIdsNotInError = createSelector(
    selectBetBuilderCombinations,
    combinations => combinations.reduce(
        (accCombinations, { id, selections, errors }) => {
            if (errors?.length) {

                return accCombinations;
            }

            return { ...accCombinations, [id]: selections };
        },
        {},
    ),
);
